import type { TaskerTask } from "../../../services/taskerService";

// Restrict to entries with role === "ASSIGNEE". Tasker uses "WATCHER" (and
// other roles) for adjunct visibility; those users shouldn't get the reviewer
// action UI. Matches primaryAssigneeName's own ASSIGNEE-first pick.
export function isCurrentUserAssigned(
  task: TaskerTask,
  currentUserId: string | undefined,
): boolean {
  if (!currentUserId) return false;
  return Boolean(
    task.assignee_ids?.some(
      (a) => a.id === currentUserId && a.role === "ASSIGNEE",
    ),
  );
}

// Pure resolver for ReviewerRouter's top-level flow: authorize → task-id →
// task-load → post-submit overlay → assignee → state-specific pane. Extracted
// so ReviewerRouter and reviewerAccessGate.test.ts consume the same ordering
// — otherwise a re-flip of the ACTIVE case (as happened in PR #164 r1 + #165
// r1) can regress the assignee gate silently.
export type ReviewerRouteOutcome =
  | "unauthorized"
  | "missing_task_id"
  | "loading"
  | "load_error"
  | "submitted_overlay"
  | "not_assigned"
  | "active_panel"
  | "reviewer_body";

export type ReviewerRouteInputs = {
  authorized: boolean;
  taskId: string | null;
  taskLoading: boolean;
  taskErrored: boolean;
  task: TaskerTask | null;
  submitted: boolean;
  currentUserId?: string;
};

export function resolveReviewerRoute({
  authorized,
  taskId,
  taskLoading,
  taskErrored,
  task,
  submitted,
  currentUserId,
}: ReviewerRouteInputs): ReviewerRouteOutcome {
  if (!authorized) return "unauthorized";
  if (!taskId) return "missing_task_id";
  if (taskLoading) return "loading";
  if (taskErrored || !task) return "load_error";
  if (submitted) return "submitted_overlay";
  if (!isCurrentUserAssigned(task, currentUserId)) return "not_assigned";
  if (task.current_status === "ACTIVE") return "active_panel";
  return "reviewer_body";
}
